import type { DeviceFingerprint } from '@/services/interfaces/FreeTrial';

/**
 * 生成设备指纹
 */
export function generateDeviceFingerprint(): DeviceFingerprint {
  const fingerprint: DeviceFingerprint = {
    userAgent: navigator.userAgent,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    colorDepth: screen.colorDepth,
    hardwareConcurrency: navigator.hardwareConcurrency,
    cookieEnabled: navigator.cookieEnabled,
  };

  // 添加设备内存信息（如果可用）
  if ('deviceMemory' in navigator) {
    fingerprint.deviceMemory = (navigator as any).deviceMemory;
  }

  return fingerprint;
}

/**
 * 生成指纹哈希（加入盐值）
 */
export async function generateFingerprintHash(fingerprint: DeviceFingerprint): Promise<string> {
  // 获取环境变量中的盐值
  const salt = import.meta.env.VITE_FINGERPRINT_SALT;
  
  // 对指纹对象进行排序序列化
  const fingerprintString = JSON.stringify(fingerprint, Object.keys(fingerprint).sort());
  
  // 将盐值和指纹字符串组合
  const dataWithSalt = salt + '|' + fingerprintString;
  
  const encoder = new TextEncoder();
  const data = encoder.encode(dataWithSalt);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * 生成或获取设备ID
 */
export function getOrCreateDeviceId(): string {
  const storageKey = 'device_id';
  let deviceId = localStorage.getItem(storageKey);
  
  if (!deviceId) {
    deviceId = crypto.randomUUID();
    localStorage.setItem(storageKey, deviceId);
  }
  
  return deviceId;
}

/**
 * 获取完整的设备标识符（设备ID + 指纹哈希）
 */
export async function getDeviceIdentifier(): Promise<{
  deviceId: string;
  fingerprint: DeviceFingerprint;
  fingerprintHash: string;
}> {
  const deviceId = getOrCreateDeviceId();
  const fingerprint = generateDeviceFingerprint();
  const fingerprintHash = await generateFingerprintHash(fingerprint);
  
  return {
    deviceId,
    fingerprint,
    fingerprintHash
  };
} 